import { gql, useLazyQuery, useMutation } from '@apollo/client';
import useForm, { isInvalidEmail, isMissing } from '@energiebespaarders/hooks/useForm';
import {
  Button,
  Card,
  Container,
  Input,
  SpinnerOverlay,
  TextLink,
} from '@energiebespaarders/symbols';
import { Center, Medium, Red, Small } from '@energiebespaarders/symbols/helpers';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { identifyMe } from '~/components/_app/initializeSession';
import useEventTracker from '~/hooks/useEventTracker';
import useRedirectOnAuthenticated from '~/hooks/useRedirectOnAuthenticated';
import { useActiveHouseId } from '../../hooks/useActiveHouseId';
import { useClearSession } from '../../hooks/useClearSession';
import useIsAuthenticated from '../../hooks/useIsAuthenticated';
import { useMe } from '../../hooks/useMe';
import { LOGOUT_CUSTOMER } from '../../queries/customer';
import { GET_ME } from '../../queries/initialize';
import { boxShadow } from '../../styles/mixins';
import ForgotPassModal from './ForgotPassModal';
import type {
  loginPartnerAgent,
  loginPartnerAgentVariables,
} from '../../types/generated/loginPartnerAgent';
import type { logoutCustomer } from '../../types/generated/logoutCustomer';
import type { me as meQuery } from '../../types/generated/me';
import type { ChangeEvent } from 'react';

const StyledButton = styled(Button)`
  ${boxShadow('sm')};
  position: absolute;
  bottom: 0;
  left: 50%;
  border-radius: 100px;
  transform: translate(-50%, 50%);
  z-index: 1010;
`;

const StyledLogoutMessage = styled.p`
  font-weight: 500;
  font-size: ${x => x.theme.type.scale[6]};
  color: ${x => x.theme.colors.red};

  &:hover {
    color: ${x => x.theme.colors.redDark};
    cursor: pointer;
  }
`;

const LOGIN_PARTNER_AGENT = gql`
  mutation loginPartnerAgent($email: String!, $password: String!) {
    loginPartnerAgent(email: $email, password: $password) {
      id
      firstName
      lastName
      email
      phone
      intercomHash
      office {
        id
        company {
          id
          name
        }
      }
    }
  }
`;

const LoginForm: React.FC = () => {
  useRedirectOnAuthenticated('PartnerAgent', '/samen');
  const { setMe } = useMe();
  const isAuthenticated = useIsAuthenticated();
  const { setActiveHouseId } = useActiveHouseId();
  const { clearSession } = useClearSession();
  const [logout] = useMutation<logoutCustomer>(LOGOUT_CUSTOMER, {
    onCompleted: async data => data.logoutCustomer && clearSession('/samen'),
  });
  const trackEvent = useEventTracker();

  const [errorMessage, setErrorMessage] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [forgotPassModalOpen, setForgotPassModalOpen] = useState(false);

  const toggleForgotPassModal = useCallback(() => setForgotPassModalOpen(v => !v), []);

  const [getMe, { data: meData }] = useLazyQuery<meQuery>(GET_ME, {
    fetchPolicy: 'network-only',
    onCompleted: ({ me }) => {
      if (me.__typename === 'PartnerAgent') {
        setActiveHouseId('');
        setMe(me);
        identifyMe(me);
      }
    },
  });

  const [loginPartnerAgentMutation, { loading }] = useMutation<
    loginPartnerAgent,
    loginPartnerAgentVariables
  >(LOGIN_PARTNER_AGENT, {
    onError: error => {
      switch (true) {
        case error.message.includes('Unknown partner'):
        case error.message.includes('Invalid credentials'):
          return setErrorMessage('Gebruikersnaam en/of wachtwoord is onjuist');
        case error.message.includes('Account is not active'):
          return setErrorMessage('Dit account is niet (langer) actief');
        default:
          return setErrorMessage('Er is iets misgegaan');
      }
    },
    onCompleted: data => {
      const partnerAgent = data.loginPartnerAgent;
      setLoginSuccess(!!partnerAgent.id);
      void trackEvent('login', {
        user_id: partnerAgent.id,
        user_type: partnerAgent.__typename,
        method: 'login_form',
        email: partnerAgent.email,
        name: `${partnerAgent.firstName} ${partnerAgent.lastName}`,
        phone: partnerAgent.phone,
        intercom_hash: partnerAgent.intercomHash,
      });
    },
  });

  useEffect(() => {
    if (!meData && loginSuccess) void getMe();
  }, [getMe, meData, loginSuccess, setMe]);

  const { formState, handleChange, submitForm } = useForm<{ email: string; password: string }>({
    initialValues: { email: '', password: '' },
    handleSubmit: values => loginPartnerAgentMutation({ variables: values }),
    validate: (values, errors) => {
      if (isMissing(values.email) || isInvalidEmail(values.email)) {
        errors.email = 'Ongeldig e-mailadres';
      }
      if (isMissing(values.password)) {
        errors.password = 'Ontbreekt';
      }
      return errors;
    },
  });

  const handleChangeEmail = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleChange({ email: e.target.value });
    },
    [handleChange],
  );
  const handleChangePassword = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      handleChange({ password: e.target.value });
    },
    [handleChange],
  );
  return (
    <>
      <Container size="sm">
        <Card pt={4} p={6} mb={5} bgColor="rgba(255, 255, 255, 0.25)" color="white" zIndex={2}>
          {isAuthenticated.asCustomer && (
            <StyledLogoutMessage onClick={() => logout()}>
              Je bent ingelogd als klant. <u>Log eerst uit</u> om als partner in te loggen.
            </StyledLogoutMessage>
          )}
          {loading && <SpinnerOverlay />}
          <form onSubmit={submitForm}>
            <Input
              type="email"
              label="E-mailadres"
              labelColor="white"
              value={formState.email.value}
              touched={formState.email.touched}
              error={formState.email.error}
              onChange={handleChangeEmail}
              disabled={isAuthenticated.asCustomer}
              autoComplete="username"
            />
            <Input
              type="password"
              label="Wachtwoord"
              labelColor="white"
              value={formState.password.value}
              touched={formState.password.touched}
              error={formState.password.error}
              onChange={handleChangePassword}
              disabled={isAuthenticated.asCustomer}
              data-private="redact"
              autoComplete="current-password"
            />
            {errorMessage && (
              <Center block>
                <Red>
                  <Medium>
                    <Small>{errorMessage}</Small>
                  </Medium>
                </Red>
              </Center>
            )}
            <StyledButton
              type="submit"
              bgColor={errorMessage ? 'red' : 'orange'}
              label="Inloggen bij partnerportaal"
              px={4}
              mb={0}
              mr={0}
              data-testid="login-samen-button"
            />
          </form>
        </Card>

        <Center block>
          <TextLink onClick={toggleForgotPassModal} hoverColor="purpleDark">
            <Small>Wachtwoord vergeten?</Small>
          </TextLink>
        </Center>
      </Container>

      <ForgotPassModal isOpen={forgotPassModalOpen} closeModal={toggleForgotPassModal} />
    </>
  );
};

export default LoginForm;
